import { matchPath } from 'react-router';
import type { Dispatch } from 'redux';
import { setIn } from 'timm';
import { call, delay, put, select, takeLeading } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import { createLogger } from '@owl-lib/logger';
import type {
  AGENTIC_VERBS,
  Summary,
  Thread,
} from '../../api/claims/interface';
import type { LLMConfig } from '../../api/feature_flags/interface';
import * as searchApi from '../../api/search/client';
import { TIMEOUT_MS } from '../../api/search/client';
import { SearchType } from '../../api/search/interface';
import * as threadsApi from '../../api/threads/client';
import type claimOverviewSlice from '../../components/ClaimOverview/data/logic';
import { ChatRole } from '../../components/ClaimOverviewShell/data/owl_assist/interface';
import { globalBus } from '../../context/event-bus';
import type { ConversationalStatus } from '../chat/interface';
const logger = createLogger(__filename);

export enum SEARCH_EMIT_EVENTS {
  SearchAnswerAskOwl = 'search_answer_ask_owl',
  SearchStatusAskOwl = 'search_status_ask_owl',
  NewSearchAskOwl = 'new_search_ask_owl',
  CancelSearchAskOwl = 'cancel_search_ask_owl',
}

const MAX_THREAD_NAME_LENGTH = 100;

const truncateThreadName = (name: string): string =>
  name.length > MAX_THREAD_NAME_LENGTH
    ? `${name.slice(0, MAX_THREAD_NAME_LENGTH)}...`
    : name;

export interface SearchState {
  answer: {
    [dossier_id: string]: {
      prompt: string;
      message?: string;
      status?: 'in_progress' | 'done' | 'error';
      searchType?: SearchType;
    };
  };
  conversationalAnswer: {
    [dossier_id: string]: {
      prompt: string;
      message?: string;
      status?: 'in_progress' | 'done' | 'error';
    };
  };
  thread_messages: {
    [dossierId: string]: {
      [threadId: string]: {
        messages: {
          id: string;
          role: ChatRole;
          content: string;
          correlation_id?: string;
          data_version?: string;
        }[];
        status: 'default' | 'in_progress' | 'done' | 'error';
      };
    };
  };
  threads: {
    [dossier_id: string]: {
      [thread_id: string]: Thread;
    };
  };
  paginatedThreadIds: {
    [dossier_id: string]: string[];
  };
  threadsCount: {
    [dossier_id: string]: number;
  };
  activeThreadId: { [dossierId: string]: string };
  actions: {
    search: AsyncActionState;
    agenticSearch: AsyncActionState;
    conversationalSearch: AsyncActionState;
    getThreads: AsyncActionState;
    getMessages: AsyncActionState;
    deleteThread: AsyncActionState;
  };
}

const initialState: SearchState = {
  answer: {},
  conversationalAnswer: {},
  threads: {},
  paginatedThreadIds: {},
  threadsCount: {},
  thread_messages: {},
  activeThreadId: {},
  actions: {
    search: { status: 'uninitialized' },
    agenticSearch: { status: 'uninitialized' },
    conversationalSearch: { status: 'uninitialized' },
    getThreads: { status: 'uninitialized' },
    getMessages: { status: 'uninitialized' },
    deleteThread: { status: 'uninitialized' },
  },
};

function* search(action: {
  payload: {
    dispatch: Dispatch<any>;
    dossierId: string;
    query: string;
    dataVersion?: string;
    searchType?: SearchType;
    threadId: string | null;
    conversational?: boolean;
  };
}) {
  const { dossierId, dispatch } = action.payload;
  const socket = yield* call(searchApi.search, action.payload);

  const handleCancelSearch = (event: unknown) => {
    const { dossierId: evDossierId } = event as { dossierId: string };
    if (evDossierId !== dossierId) {
      return;
    }
    logger.info('Search cancelled.', { dossierId });
    socket.onmessage = null;
    socket.onclose = null;
    socket.close();
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'cancelled');
  };

  socket.onopen = () => {
    logger.info('WebSocket connection opened for search!');
    const payload = {
      query: action.payload.query,
      dataVersion: action.payload.dataVersion,
      searchType: action.payload.searchType,
      thread_id: action.payload.threadId,
      conversational: action.payload.conversational,
    };
    socket.send(JSON.stringify(payload));
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'in_progress');
    globalBus.emit(
      SEARCH_EMIT_EVENTS.NewSearchAskOwl,
      action.payload.searchType || SearchType.Normal
    );
  };

  socket.onmessage = (event: MessageEvent<any>) => {
    const data = JSON.parse(event.data);
    dispatch({
      type: 'search/onSearchMessage',
      payload: {
        ...data,
        dossierId,
      },
    });
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from search.');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
  };

  socket.onerror = (error) => {
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'error');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    logger.error('WebSocket Error:', error);
  };

  globalBus.on(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
}

function* conversationalSearch(action: {
  payload: {
    dispatch: Dispatch<any>;
    dossierId: string;
    query: string;
    dataVersion?: string;
    threadId: string | null;
    llmOverride?: LLMConfig;
  };
}) {
  const { dossierId, dispatch, query, dataVersion } = action.payload;
  let { threadId } = action.payload;
  if (!threadId) {
    const threadName = truncateThreadName(query);
    try {
      const response = yield* call(threadsApi.create, {
        dossierId,
        threadName,
      });
      threadId = response.thread_id;
      dispatch({
        type: 'search/setActiveThreadId',
        payload: { dossierId, threadId },
      });
    } catch (err) {
      logger.error('conversationalSearch: failed to create thread', err);
      throw err;
    }
  }
  const socket = yield* call(searchApi.search, {
    dossierId,
    query,
    dataVersion,
    threadId,
  });
  dispatch({
    type: 'search/addUserMessage',
    payload: {
      dossierId,
      threadId,
      message: query,
    },
  });

  const handleCancelSearch = (event: unknown) => {
    const { dossierId: evDossierId, threadId: evThreadId } = event as {
      dossierId: string;
      threadId?: string | null;
    };
    if (
      evDossierId !== dossierId ||
      (evThreadId != null && evThreadId !== threadId)
    ) {
      return;
    }
    logger.info('Conversational search cancelled.', { dossierId, threadId });
    socket.onmessage = null;
    socket.onclose = null;
    socket.close();
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'cancelled');
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: { dossierId, threadId, message: { data: '' }, status: 'done' },
    });
  };

  socket.onopen = () => {
    logger.info('WebSocket connection opened for conversational search!');
    const payload = {
      query: action.payload.query,
      dataVersion: action.payload.dataVersion,
      thread_id: threadId,
      conversational: true,
      llm_override: action.payload.llmOverride,
    };
    socket.send(JSON.stringify(payload));
  };

  let finalStatusReceived = false;

  socket.onmessage = (event: MessageEvent<any>) => {
    const data = JSON.parse(event.data);
    if (data.type === 'thread_created') {
      return;
    }
    if (data.status === 'done' || data.status === 'error') {
      finalStatusReceived = true;
    }
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: {
        ...data,
        dossierId,
        threadId,
      },
    });
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from conversational search.');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    if (!finalStatusReceived) {
      dispatch({
        type: 'search/onConversationalSearchMessage',
        payload: {
          dossierId,
          threadId,
          message: { data: '' },
          status: 'done',
          data_version: dataVersion,
        },
      });
    }
  };

  socket.onerror = (error) => {
    logger.error('WebSocket Error:', error);
    finalStatusReceived = true;
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: {
        dossierId,
        threadId,
        message: { data: '' },
        status: 'error',
      },
    });
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
  };
  globalBus.on(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
}

function* agenticSearch(action: {
  payload: {
    dispatch: Dispatch<any>;
    dossierId: string;
    selected_text: string;
    summary: Summary;
    data_version?: string;
    verbs: AGENTIC_VERBS[];
    threadId: string | null;
  };
}) {
  const { dispatch, dossierId, selected_text } = action.payload;
  let { threadId } = action.payload;
  if (!threadId) {
    const threadName = truncateThreadName(`Expanded on ${selected_text}`);
    try {
      const response = yield* call(threadsApi.create, {
        dossierId,
        threadName,
      });
      threadId = response.thread_id;
      dispatch({
        type: 'search/setActiveThreadId',
        payload: { dossierId, threadId },
      });
    } catch (err) {
      logger.error('agenticSearch: failed to create thread', err);
      throw err;
    }
  }
  const payload = {
    dossierId,
    selected_text: action.payload.selected_text,
    summary: action.payload.summary,
    data_version: action.payload.data_version,
    verbs: action.payload.verbs,
    thread_id: threadId,
  };
  const socket = yield* call(searchApi.agenticSearch, payload);

  dispatch({
    type: 'search/addUserMessage',
    payload: {
      dossierId,
      threadId,
      message: `Expanding on "${selected_text}"`,
    },
  });

  const handleCancelSearch = (event: unknown) => {
    const { dossierId: evDossierId, threadId: evThreadId } = event as {
      dossierId: string;
      threadId?: string | null;
    };
    if (
      evDossierId !== dossierId ||
      (evThreadId != null && evThreadId !== threadId)
    ) {
      return;
    }
    logger.info('Agentic search cancelled.', { dossierId, threadId });
    socket.onmessage = null;
    socket.onclose = null;
    socket.close();
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'cancelled');
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: { dossierId, threadId, message: { data: '' }, status: 'done' },
    });
  };

  socket.onopen = () => {
    logger.info('WebSocket connection opened for agentic search');
    socket.send(JSON.stringify(payload));
  };

  let finalStatusReceived = false;

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    if (data.type === 'thread_created') {
      return;
    }
    if (data.status === 'done' || data.status === 'error') {
      finalStatusReceived = true;
    }
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: {
        ...data,
        dossierId,
        threadId,
      },
    });
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from agentic search');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    if (!finalStatusReceived) {
      dispatch({
        type: 'search/onConversationalSearchMessage',
        payload: {
          dossierId,
          threadId,
          message: '',
          status: 'done',
          data_version: action.payload.data_version,
        },
      });
    }
  };

  socket.onerror = (error) => {
    finalStatusReceived = true;
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: {
        dossierId,
        threadId,
        message: { data: '' },
        status: 'error',
      },
    });
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
    logger.error('WebSocket Error in agentic search:', error);
  };

  globalBus.on(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
}

const GC_INTERVAL_MS = 15 * 60_000;
const GC_THRESHOLD_MS = 15 * 60_000;
const DOSSIER_ROUTE_PATTERNS = ['/claims/:dossierId/*'];
const GC_KEYS = [
  'answer',
  'conversationalAnswer',
  'thread_messages',
  'threads',
  'activeThreadId',
];

function* garbageCollect() {
  while (true) {
    yield delay(GC_INTERVAL_MS);
    const lastViewed: Record<string, number> = yield* select(
      // typed against the slice directly: importing StoreState here creates a
      // store.ts <-> search/logic.ts type cycle that widens this slice's name
      (s: { 'claim-overview': (typeof claimOverviewSlice)['initialState'] }) =>
        s['claim-overview'].lastViewed
    );
    const currentDossierId = DOSSIER_ROUTE_PATTERNS.map((p) =>
      matchPath(p, window.location.pathname)
    ).find((m) => m !== null)?.params.dossierId;

    const now = Date.now();
    const staleDossiers = Object.entries(lastViewed)
      .filter(([id, t]) => id !== currentDossierId && now - t > GC_THRESHOLD_MS)
      .map(([id]) => id);

    yield put({
      type: 'search/onGarbageCollect',
      payload: { dossierIds: staleDossiers },
    });
  }
}

const slice = setupSlice('search', initialState)
  .addAsyncSagas(
    {
      search,
      conversationalSearch,
      agenticSearch,
      getThreads: invoke(threadsApi.getThreads),
      getMessages: invoke(threadsApi.getMessages),
      deleteThread: invoke(threadsApi.deleteThread),
    },
    {
      timeoutLimit: TIMEOUT_MS,
    }
  )
  .addAsyncSagas(
    {
      garbageCollect,
    },
    {
      timeoutLimit: -1,
      scheduler: takeLeading,
    }
  )
  .addReducers({
    'search/pending': (state, action) => {
      state.answer = setIn(state.answer, [action.meta.arg.dossierId], {
        prompt: action.meta.arg.query,
        searchType: action.meta.arg.searchType,
        message: undefined,
        status: 'in_progress',
      }) as typeof state.answer;
    },
    'conversationalSearch/pending': (state, action) => {
      state.conversationalAnswer = setIn(
        state.conversationalAnswer,
        [action.meta.arg.dossierId],
        {
          prompt: action.meta.arg.query,
          message: undefined,
          status: 'in_progress',
        }
      ) as typeof state.conversationalAnswer;
    },
    'conversationalSearch/rejected': (state, action) => {
      state.conversationalAnswer = setIn(
        state.conversationalAnswer,
        [action.meta.arg.dossierId, 'status'],
        'error'
      ) as typeof state.conversationalAnswer;
    },
    'getThreads/fulfilled': (state, action) => {
      const { dossierId, offset } = action.meta.arg;
      const threads: Thread[] = action.payload.threads;
      const newThreads = Object.fromEntries(
        threads.map((thread) => [thread.thread_id, thread])
      );
      const newIds = threads.map((thread) => thread.thread_id);

      if (!offset) {
        state.threads[dossierId] = newThreads;
        state.paginatedThreadIds[dossierId] = newIds;
      } else {
        state.threads[dossierId] = {
          ...state.threads[dossierId],
          ...newThreads,
        };
        state.paginatedThreadIds[dossierId] = [
          ...new Set([
            ...(state.paginatedThreadIds[dossierId] ?? []),
            ...newIds,
          ]),
        ];
      }
      if (typeof action.payload.count === 'number') {
        state.threadsCount[dossierId] = action.payload.count;
      }
    },
    'getMessages/fulfilled': (state, action) => {
      const { dossierId, threadId } = action.meta.arg;
      const messages = action.payload.messages;
      if (!state.thread_messages[dossierId]) {
        state.thread_messages[dossierId] = {};
      }

      state.thread_messages[dossierId][threadId] = {
        messages: messages.map((m: any) => ({
          id: m.message_id,
          role: m.role === 'ASSISTANT' ? ChatRole.Assistant : ChatRole.User,
          content: m.message.content,
          correlation_id: m.message.correlation_id,
          data_version: m.message.data_version,
        })),
        status: 'done',
      };
    },
    'deleteThread/fulfilled': (state, action) => {
      const { dossierId, threadId } = action.meta.arg;
      if (state.threads[dossierId] && state.threads[dossierId][threadId]) {
        const updatedThreads = { ...state.threads[dossierId] };
        delete updatedThreads[threadId];
        state.threads = setIn(
          state.threads,
          [dossierId],
          updatedThreads
        ) as typeof state.threads;
      }
      const ids = state.paginatedThreadIds[dossierId];
      if (ids?.includes(threadId)) {
        state.paginatedThreadIds[dossierId] = ids.filter(
          (id) => id !== threadId
        );
      }
      if (typeof state.threadsCount[dossierId] === 'number') {
        state.threadsCount[dossierId] = Math.max(
          0,
          state.threadsCount[dossierId] - 1
        );
      }
    },
  })
  .addReducers({
    onSearchMessage: (
      state,
      action: {
        payload: {
          dossierId: string;
          message: string;
          status: 'in_progress' | 'done' | 'error';
        };
      }
    ) => {
      const { dossierId, message, status } = action.payload;
      if (status === 'in_progress' && message !== '<<HEART_BEAT>>') {
        state.answer = setIn(
          state.answer,
          [dossierId, 'message'],
          `${state.answer[dossierId]?.message ?? ''}${message}`
        ) as typeof state.answer;
      }

      state.answer = setIn(
        state.answer,
        [dossierId, 'status'],
        status
      ) as typeof state.answer;
    },
    addUserMessage: (
      state,
      action: {
        payload: {
          dossierId: string;
          threadId: string;
          message: string;
        };
      }
    ) => {
      const { dossierId, threadId, message } = action.payload;

      if (!state.thread_messages[dossierId]) {
        state.thread_messages[dossierId] = {};
      }
      if (!state.thread_messages[dossierId][threadId]) {
        state.thread_messages[dossierId][threadId] = {
          messages: [],
          status: 'default',
        };
      }

      state.thread_messages[dossierId][threadId].messages.push({
        id: uuid(),
        role: ChatRole.User,
        content: message,
      });

      state.thread_messages[dossierId][threadId].status = 'in_progress';
    },
    onConversationalSearchMessage: (
      state,
      action: {
        payload: {
          dossierId: string;
          threadId: string;
          message: string;
          status: ConversationalStatus;
          correlation_id?: string;
          data_version?: string;
        };
      }
    ) => {
      const {
        dossierId,
        threadId,
        message,
        status,
        correlation_id,
        data_version,
      } = action.payload;

      const thread = state.thread_messages[dossierId]?.[threadId];
      if (!thread) {
        return;
      }

      if (status === 'done' || status === 'error') {
        thread.status = status;
        if (correlation_id || data_version) {
          const lastAssistantMessage = thread.messages
            .slice()
            .reverse()
            .find((m) => m.role === ChatRole.Assistant);
          if (lastAssistantMessage) {
            if (correlation_id) {
              lastAssistantMessage.correlation_id = correlation_id;
            }
            if (data_version) {
              lastAssistantMessage.data_version = data_version;
            }
          }
        }
        return;
      }

      if (status === 'in_progress' && message !== '<<HEART_BEAT>>') {
        const last = thread.messages.at(-1);

        if (!last || last.role === ChatRole.User) {
          thread.messages.push({
            id: uuid(),
            role: ChatRole.Assistant,
            content: message,
          });
        } else {
          thread.messages[thread.messages.length - 1] = {
            ...last,
            content: last.content + message,
          };
        }
      }

      thread.status = status;
    },
    setActiveThreadId: (
      state,
      action: { payload: { dossierId: string; threadId: string | null } }
    ) => {
      const { dossierId, threadId } = action.payload;
      if (threadId) {
        state.activeThreadId[dossierId] = threadId;
      } else {
        delete state.activeThreadId[dossierId];
      }
    },
    onGarbageCollect: (
      state,
      action: { payload: { dossierIds: string[] } }
    ) => {
      for (const id of action.payload.dossierIds) {
        for (const key of GC_KEYS) {
          delete state[key][id];
        }
      }
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
